/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import * as log from "loglevel";
import Logger = log.Logger;
import {generateUUID} from "../Common";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {UiFieldConfig, UiFieldSubCommandHandler} from "../../generated/UiFieldConfig";
import {TeamAppsEvent} from "../util/TeamAppsEvent";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";

/**
 * @author Yann Massard (yamass@gmail.com)
 */

export interface ValueChangeEventData {
	value: any
	redundancyKey?: string;
}

export abstract class UiField<C extends UiFieldConfig = UiFieldConfig, V = any> implements UiFieldSubCommandHandler{

	public readonly onValueChanged: TeamAppsEvent<ValueChangeEventData> = new TeamAppsEvent<ValueChangeEventData>(this);
	public readonly onUserManipulation: TeamAppsEvent<void> = new TeamAppsEvent<void>(this);
	public readonly onVisibilityChanged: TeamAppsEvent<boolean> = new TeamAppsEvent<boolean>(this);

	public static editingModeCssClasses: { [x: number]: string } = {
		[UiFieldEditingMode.EDITABLE]: "editable",
		[UiFieldEditingMode.EDITABLE_IF_FOCUSED]: "editable-if-focused",
		[UiFieldEditingMode.DISABLED]: "disabled",
		[UiFieldEditingMode.READONLY]: "readonly"
	};

	protected logger: Logger = log.getLogger(((<any>(this.constructor)).name || this.constructor.toString().match(/\w+/g)[1]));

	private valueChangeRedundancyKey: string = generateUUID();

	private committedValue: V;

	private editingMode: UiFieldEditingMode;

	private _attachedToDom = false;

	private visible = true;

	constructor(protected _config: C,
	            targetElement: Element,
	            protected _context: TeamAppsUiContext) {
		this.initialize(_config, targetElement, _context);
		this.setEditingMode(_config.editingMode);
		this.setCommittedValue(this.getDefaultValue());
		this.setVisible(_config.visible, false);
		this.onValueChanged.addListener(() => this.onUserManipulation.fire(null))
	}

	protected abstract initialize(config: C, targetElement: Element, context: TeamAppsUiContext): void;

	public getTeamAppsType(): string {
		return this._config._type;
	}

	get attachedToDom() {
		return this._attachedToDom;
	}

	set attachedToDom(attachedToDom) {
		let wasAttachedToDom = this._attachedToDom;
		this._attachedToDom = attachedToDom;
		if (attachedToDom && !wasAttachedToDom) {
			this.onAttachedToDom();
		}
	}

	abstract getMainDomElement(): JQuery;

	abstract getFocusableElement(): JQuery;

	public hasFocus() {
		let focusableElement = this.getFocusableElement();
		return focusableElement && focusableElement.is(":focus");
	}

	public focus(): void {
		let focusableElement = this.getFocusableElement();
		focusableElement && focusableElement.focus();
	}

	abstract destroy(): void;

	abstract isValidData(v: V): boolean;

	abstract getTransientValue(): V;

	abstract getDefaultValue(): V; // the value to be set if no other value has been set.

	protected abstract displayCommittedValue(): void;

	public abstract valuesChanged(v1: V, v2: V): boolean;

	// abstract static getReadOnlyHtml(); // every UiField class needs to provide such a static method!

	public setCommittedValue(v: V): void {
		if (!this.isValidData(v)) {
			this.logger.error(this.constructor.toString().match(/\w+/g)[1] + ": Invalid data: " + JSON.stringify(v));
			return;
		}
		this.logger.debug("setCommittedValue(): " + JSON.stringify(v));
		this.committedValue = v != null ? v : this.getDefaultValue();
		this.displayCommittedValue();
	}

	public getCommittedValue(): V {
		return this.committedValue;
	}

	public onResize(): void {
		// do nothing (default implementation)
	}
	
	private fireCommittedChangeEvent(): void {
		this.logger.trace("firing committed change event: " + JSON.stringify(this.getCommittedValue()));
		this.onValueChanged.fire({
			value: this.committedValue,
			redundancyKey: this.valueChangeRedundancyKey
		});
	}

	public commit(forceEvenIfNotChanged?: boolean): boolean {
		let value = this.getTransientValue();
		let changed = this.valuesChanged(value, this.committedValue);
		if (changed || forceEvenIfNotChanged) {
			this.committedValue = value;
			this.fireCommittedChangeEvent();
		}
		return changed;
	}

	/**
	 * Override this method to execute code that cannot be executed before the field is attached to the DOM.
	 *
	 * Example 1:
	 *   An HTML/Flash video player with autoplay = true will not be able to play until it is attached to the DOM.
	 *   So the code for starting the video will need to be executed in this (overwritten) method.
	 * Example 2:
	 *   UiTable uses slickgrid. slickgrid can only initialize correctly when attached to the DOM.
	 *
	 * Components containing other components should set attachedToDom on their child components.
	 * This will in turn invoke this method on the children, so the call propagates recursively.
	 *
	 * Example:
	 *   A component with child components will call "child.attachedToDom = true" on its children.
	 */
	protected onAttachedToDom(): void {
		// do nothing (default implementation)
	}

	public setEditingMode(editingMode: UiFieldEditingMode): void {
		const oldEditingMode = this.editingMode;
		this.editingMode = editingMode;
		this.onEditingModeChanged(editingMode, oldEditingMode);
	}

	protected abstract onEditingModeChanged(editingMode: UiFieldEditingMode, oldEditingMode?: UiFieldEditingMode): void;

	public getEditingMode(): UiFieldEditingMode {
		return this.editingMode;
	}

	public isEditable(): boolean {
		return this.getEditingMode() === UiFieldEditingMode.EDITABLE || this.getEditingMode() === UiFieldEditingMode.EDITABLE_IF_FOCUSED;
	}

	public static defaultOnEditingModeChangedImpl(field: UiField<UiFieldConfig, any>) {
		let $wrapper = field.getMainDomElement();
		$wrapper.removeClass(
			Object.keys(UiField.editingModeCssClasses)
				.map((key: any) => UiField.editingModeCssClasses[key])
				.join(" ")
		)
			.addClass(UiField.editingModeCssClasses[field.getEditingMode()]);

		let $focusable = field.getFocusableElement();
		if ($focusable) {
			switch (field.getEditingMode()) {
				case UiFieldEditingMode.EDITABLE:
					$focusable.prop("readonly", false);
					$focusable.prop("disabled", false);
					$focusable.attr("tabindex", "0");
					break;
				case UiFieldEditingMode.EDITABLE_IF_FOCUSED:
					$focusable.prop("readonly", false);
					$focusable.prop("disabled", false);
					$focusable.attr("tabindex", "0");
					break;
				case UiFieldEditingMode.DISABLED:
					$focusable.prop("readonly", false);
					$focusable.prop("disabled", true);
					break;
				case UiFieldEditingMode.READONLY:
					$focusable.prop("readonly", true);
					$focusable.prop("disabled", false);
					$focusable.attr("tabindex", "-1");
					break;
				default:
					log.getLogger("UiField").error("unknown editing mode! " + field.getEditingMode());
			}
		}
	}

	public isVisible() {
		return this.visible;
	}

	public setVisible(visible: boolean, fireEvent = true) {
		this.visible = visible;
		this.getMainDomElement().toggleClass("hidden", !visible);
		if (fireEvent) {
			this.onVisibilityChanged.fire(visible);
		}
	}
}
